import { Loader } from '@googlemaps/js-api-loader';
import { useRef } from 'react';
import { useState, useEffect } from 'react';
import { fetchUser } from '../../providers/auth';
import { fetchCities, fetchDistricts } from '../../providers/locations';
import { fetchUsers, ROLE_ADMIN, ROLE_CLINIC } from '../../providers/users';
import { Select, Input, Label, Field, TextArea } from '../Field';

function GeneralTab({ clinic, setClinic, t }) {
    const googleMapsInput = useRef(null);
    const [user, setUser] = useState({});
    const [districts, setDistricts] = useState([]);
    const [cities, setCities] = useState([]);
    const [users, setUsers] = useState([]);

    const changeHandler = (e) => {
        const { name, value } = e.target;
        setClinic((current) => ({ ...current, [name]: value }));
    };

    useEffect(() => {
        fetchUser().then((user) => {
            setUser(user);
            if (user.role == ROLE_ADMIN) {
                fetchUsers({ role: ROLE_CLINIC })
                    .then((users) => users?.map(({ id, email }) => ({ id, name: email })))
                    .then(setUsers);
            }
        });
        fetchDistricts().then(setDistricts);
    }, []);

    useEffect(() => {
        const { district_id } = clinic;
        if (district_id) {
            fetchCities({ district_id }).then(setCities);
        } else {
            setCities([]);
        }
    }, [clinic.district_id]);

    useEffect(() => {
        const googleMapsApiKey = document.querySelector('meta[name="google-maps-api-key"]')?.content;

        if (!googleMapsApiKey) return;

        const googleMapsLoader = new Loader({
            apiKey: googleMapsApiKey,
            libraries: ['places'],
        });

        googleMapsLoader
            .load()
            .then((google) => {
                const autocomplete = new google.maps.places.Autocomplete(googleMapsInput.current, {
                    fields: ['place_id', 'geometry'],
                    componentRestrictions: { country: 'pt' },
                });
                autocomplete.addListener('place_changed', () => {
                    const place = autocomplete.getPlace();
                    if (place.place_id) {
                        setClinic((current) => ({
                            ...current,
                            google_maps_id: place.place_id,
                            google_maps_label: googleMapsInput.current.value,
                            latitude: place.geometry?.location?.lat(),
                            longitude: place.geometry?.location?.lng(),
                        }));
                    }
                });
                googleMapsInput.current.addEventListener('keydown', (event) => {
                    if (event.keyCode === 13) {
                        event.preventDefault();
                    }
                });
            })
            .catch((error) => {
                console.error(error);
            });
    }, []);

    return (
        <div className="space-y-2">
            <Field>
                <Label>
                    <span>{t('clinic-name')} *</span>
                    <Input type="text" name="name" value={clinic.name || ''} onChange={changeHandler} />
                </Label>
            </Field>

            <Field>
                <Label>
                    <span>{t('bio')} (Português)</span>
                    <TextArea name="description[pt]" defaultValue={clinic.description_pt} />
                </Label>
            </Field>

            <Field>
                <Label>
                    <span>{t('bio')} (English)</span>
                    <TextArea name="description[en]" defaultValue={clinic.description_en} />
                </Label>
            </Field>

            <Field>
                <Label>
                    <span>{t('bio')} (Français)</span>
                    <TextArea name="description[fr]" defaultValue={clinic.description_fr} />
                </Label>
            </Field>

            <Field>
                <Label>
                    <span>{t('email')} *</span>
                    <Input type="email" name="email" value={clinic.email || ''} onChange={changeHandler} />
                </Label>
            </Field>

            <Field>
                <Label>
                    <span>{t('tin')} *</span>
                    <Input type="text" name="tin" value={clinic.tin || ''} onChange={changeHandler} />
                </Label>
            </Field>

            <Field>
                <Label>
                    <span>{t('phone')} *</span>
                    <Input type="text" name="phone" value={clinic.phone || ''} onChange={changeHandler} />
                </Label>
            </Field>

            <Field>
                <Label>
                    <span>{t('website')}</span>
                    <Input type="text" name="website" value={clinic.website || ''} onChange={changeHandler} />
                </Label>
            </Field>

            <Field>
                <Label>
                    <span>{t('address')}</span>
                    <Input type="text" name="address" value={clinic.address || ''} onChange={changeHandler} />
                </Label>
            </Field>

            <Field>
                <Label>
                    <span>{t('district')}</span>
                    <Select name="district_id" value={clinic.district_id || ''} onChange={changeHandler} options={districts} />
                </Label>
            </Field>

            <Field>
                <Label>
                    <span>{t('city')}</span>
                    <Select name="city_id" value={clinic.city_id || ''} onChange={changeHandler} options={cities} disabled={!cities.length} />
                </Label>
            </Field>

            {/* <Field>
                <Label>
                    <span>Google Maps</span>
                    <Input type="text" name="google_maps" value={clinic.google_maps || ''} onChange={changeHandler} />
                </Label>
            </Field> */}

            <Field>
                <Label>
                    <span>Google Maps</span>
                    <Input type="text" ref={googleMapsInput} defaultValue={clinic?.google_maps_label} placeholder={t('search-location')} />
                    <input type="hidden" name="google_maps_label" defaultValue={clinic?.google_maps_label || ''} />
                    <input type="hidden" name="google_maps_id" defaultValue={clinic?.google_maps_id || ''} />
                    <input type="hidden" name="latitude" defaultValue={clinic?.latitude || ''} />
                    <input type="hidden" name="longitude" defaultValue={clinic?.longitude || ''} />
                    {clinic.google_maps_id ? (
                        <div>
                            <div className="mr-6 inline-block">
                                <span>{t('selected-location')}: </span>
                                <span>{clinic.google_maps_label}</span>
                            </div>
                            <button
                                type="button"
                                className="hover:underline"
                                onClick={() => {
                                    googleMapsInput.current.value = '';
                                    setClinic((current) => ({
                                        ...current,
                                        google_maps_id: '',
                                        google_maps_label: '',
                                        latitude: '',
                                        longitude: '',
                                    }));
                                }}
                            >
                                {t('clear-location')}
                            </button>
                        </div>
                    ) : (
                        <div>{t('no-location-selected')}</div>
                    )}
                </Label>
            </Field>

            {user?.role == ROLE_ADMIN && (
                <>
                    <Field>
                        <Label>
                            <span>{t('user-account')}</span>
                            <Select name="user_id" value={clinic.user_id || ''} onChange={changeHandler} options={users} />
                        </Label>
                    </Field>
                </>
            )}
        </div>
    );
}

export default GeneralTab;
